import {Component, Object3D, Emitter} from '@wonderlandengine/api';
import {StatNames} from '../enums/StatNames.js';
//import {EventEmitter} from '../utils/EventEmitter.js';
import {saveGame} from '@wonderlandengine/upsdk';
import {TransactionalNames} from '../enums/TransactionalNames.js';
import {BooleanName} from '../enums/BooleanName.js';

/**
 * Statistics are used for the meta game.
 *
 * Note:
 * There used to be a whole transaction system in the original game. This is no longer needed. We
 * are not implementing the feature that users can take their save games to other platforms.
 * The data is stored as savegames using the UpSDK. This means that there might also be changes in the
 * way the data is saved.
 *
 * The original game use a `BackendInterface`. This is completely removed. But, the Statistics component
 * is kept similar to the original implementation to make it easier to keep track of what is happening
 * and what might be needed while porting the game.
 *
 * After the game is fully ported these calls regarding 'transactions' should be removed.
 *
 * TK Temporary Notes/Questions:
 * - Why is the _stats a map of string and not the enum? This might be improved in TS
 *   Maybe just replace it with the SaveGame object. All data should be in there anyway. Then figure out
 *   the rest in this class, so the rest of the application just get the values they need.
 *   For now I go with separating the data into their enum values, in a save game object.
 */
export class Statistics extends Component {
    static TypeName = 'Statistics';

    // Singleton
    private static _instance: Statistics;
    static get instance(): Statistics {
        return Statistics._instance;
    }

    private static _networkWriteDelay = 30.0;
    private static saveDelay = 1.0;

    private _stats = new SaveGameData();
    private _currentConfig: Map<string, {}> = new Map();
    private _updateCallbacks: Emitter<[Statistics]> = new Emitter();
    private _changedCallbacks: Emitter<[Statistics]> = new Emitter();
    private _configChangedCallbacks: Emitter<[Map<string, {}>]> = new Emitter();

    init() {
        if (Statistics._instance) {
            console.error('There can only be one instance of Statistics Component');
        }
        Statistics._instance = this;
        this._load();
    }

    registerUpdateCallback(callback: () => void) {
        this._updateCallbacks.add(callback);
    }
    unRegisterUpdateCallback(callback: () => void) {
        this._updateCallbacks.remove(callback);
    }
    registerChangedCallback(callback: () => void) {
        this._changedCallbacks.add(callback);
    }
    unRegisterChangedCallback(callback: () => void) {
        this._changedCallbacks.remove(callback);
    }
    registerConfigChangedCallback(callback: (newConfig: Map<string, {}>) => void) {
        this._configChangedCallbacks.add(callback);
    }
    unRegisterConfigChangedCallback(callback: (newConfig: Map<string, {}>) => void) {
        this._configChangedCallbacks.remove(callback);
    }

    static statNamesToString(name: StatNames): string {
        return StatNames[name];
    }

    static transactionalNamesToString(name: TransactionalNames): string {
        return TransactionalNames[name];
    }

    getLong(name: StatNames): number {
        return this.getLongByString(Statistics.statNamesToString(name));
    }

    getLongByString(name: string, ignoreCache = false): number {
        return this._stats.data[name] ?? 0;
    }

    record(name: StatNames, value: number) {
        const stringName = Statistics.statNamesToString(name);
        this._stats.data[stringName] = value;
        this._statsChanged();
    }

    recordAdditive(name: StatNames, value: number) {
        const stringName = Statistics.statNamesToString(name);
        const finalValue = this.getLong(name) + value;
        this._stats.data[stringName] = finalValue;
        this._statsChanged();
    }

    recordHighest(name: StatNames, value: number): boolean {
        let changed = false;
        const stringName = Statistics.statNamesToString(name);
        const record = this.getLong(name);

        if (record < value) {
            this._stats.data[stringName] = value;
            this._statsChanged();
            changed = true;
        }
        return changed;
    }

    recordByString(name: string, value: number) {
        this._stats.data[name] = value;
        this._statsChanged();
    }

    /**
     * Get the amount of a transactional (a bought) item the player has.
     * @param name Transactional name
     * @returns the amount of the item the player has
     */
    getTransactional(name: TransactionalNames): number {
        const stringName = TransactionalNames[name];
        const value = parseInt(this._stats.data[stringName] ?? '0') ?? 0;
        return value;
    }

    /**
     * Perform a transaction. This is storing the purchase of items.
     * There used to be a transaction being kept in the original, this is no longer the case.
     * The rebuild of the game uses this to update the amount of items the player has, and the
     *
     * @param stat For example BombUpgrade
     * @param newValue The total new amount of the item the player has after the transaction.
     * @param currency Technically the currency, but there's only Coins.
     * @param amount The amount of currency used for the transaction. This is negative if the player spent coins.
     */
    addPurchaseTransaction(
        stat: TransactionalNames,
        newValue: number,
        currency: TransactionalNames,
        amount: number
    ) {
        const stringName = TransactionalNames[stat];
        this._stats.data[stringName] = newValue;

        let currencyValue = this.getTransactional(currency);
        currencyValue += amount;
        const currencyStringName = TransactionalNames[currency];
        this._stats.data[currencyStringName] = currencyValue;

        this._statsChanged();
        // string stringName = TransactionalNamesToString(stat);
        // // save the new value but also record the delta
        // long oldValue = GetTransactional(stat);
        // stats[stringName] = newValue;
        // long change = newValue - oldValue;
        // long currencyValue = 0L;
        // string currencyStringName = TransactionalNamesToString(currency);
        // object record;
        // if (stats.TryGetValue(currencyStringName, out record))
        // {
        // 	if (record is long)
        // 	{
        // 		currencyValue = (long)record;
        // 	}
        // 	else
        // 	{
        // 		currencyValue = (int)record;
        // 	}
        // 	currencyValue += amount;
        // 	stats[currencyStringName] = currencyValue;
        // }
        // else if (amount >= 0)
        // {
        // 	stats[currencyStringName] = amount;
        // }
        // Transaction newTransaction = new Transaction(stringName, change, newValue, currencyStringName, amount);
        // transactions.Add(newTransaction);
        // StatsChanged(false);
        //Logger.Log("New Transaction:" + stringName + " = " + newValue + " (change: " + change + ") for " + currencyStringName + " change: " + amount + " (" + currencyValue + ")");
    }

    addAnonymousPurchaseTransaction(stat: TransactionalNames, newValue: number) {
        // TK: Maybe we need to call to the UberSDK to actually 'buy' the coins.
        const stringName = TransactionalNames[stat];
        const finalValue = this.getLongByString(stringName) + newValue;
        this._stats.data[stringName] = finalValue;
        this._statsChanged();
    }

    updateConfig(newState: Map<string, {}>) {
        if (newState.has('saveWriteFrequency')) {
            let value = newState['saveWriteFrequency'].ToString();
            Statistics._networkWriteDelay = parseFloat(value);
        }
        this._configChangedCallbacks.notify(newState);
        // for (int x = 0; x < configChangedCallbacks.Count; x++)
        // {
        // 	configChangedCallbacks[x](newState);
        // }
    }

    getConfig(): Map<string, {}> {
        return this._currentConfig;
    }

    private _load() {
        let sg: SaveGameData = saveGame.load();
        // Do some checking or something...
        this._stats = sg ?? new SaveGameData();
    }
    private _save() {
        saveGame.save(this._stats);
    }

    private _statsChanged() {
        this._save();
        this._notifyChanged();
    }

    private _notifyChanged() {
        this._changedCallbacks.notify(this);
    }
}
type KeyNames =
    | keyof typeof StatNames
    | keyof typeof TransactionalNames
    | keyof typeof BooleanName;

class SaveGameData {
    data: Partial<Record<KeyNames, number>> = {};
}
